import cn from '../utils/cn';

function getInitials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

const SIZES = {
  sm: 'w-8 h-8 text-xs',
  md: 'w-9 h-9 text-sm',
  lg: 'w-11 h-11 text-base',
};

/**
 * Presentational avatar for the navbar / user dropdown.
 * Renders initials only (colored circle) with an optional image hook.
 * When `impersonating` is true, shows a small red dot on the avatar's
 * corner so the admin never forgets they are impersonating another user.
 */
export default function KoobNavbarAvatar({
  name,
  // Optional image hook for the future — no image lookup is done yet.
  imageUrl,
  impersonating = false,
  size = 'md',
  className,
}: {
  name?: string;
  imageUrl?: string;
  impersonating?: boolean;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const initials = getInitials(name);
  const sizeClasses = SIZES[size] || SIZES.md;

  return (
    <span className={cn('relative inline-flex shrink-0', className)}>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={name || ''}
          className={cn('rounded-full object-cover', sizeClasses)}
        />
      ) : (
        <span
          className={cn(
            'flex items-center justify-center rounded-full text-white font-semibold',
            sizeClasses,
          )}
          style={{ background: 'linear-gradient(to bottom, #F68B11, #F67008)' }}
        >
          {initials || <i className="far fa-user fa-fw" />}
        </span>
      )}

      {/* Corner dot — only while impersonating */}
      {impersonating && (
        <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white" />
      )}
    </span>
  );
}
